import { useState, useEffect, useCallback, useRef, createContext, useContext, type ReactNode, createElement } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/lib/auth';
import type { Json } from '@/integrations/supabase/types';

export type OnboardingFlowType = 'full' | 'invited' | 'none';

interface OnboardingState {
  showOnboarding: boolean;
  onboardingType: OnboardingFlowType;
  isLoading: boolean;
  libraryCount: number;
  projectCount: number;
  completeOnboarding: () => Promise<void>;
  replayTutorial: () => Promise<void>;
  developerReset: () => Promise<void>;
}

const OnboardingContext = createContext<OnboardingState | null>(null);

/**
 * Public hook — reads onboarding state from the shared context.
 * All consumers (AppLayout, nav, settings) get the SAME instance,
 * so calling replayTutorial() from any consumer immediately updates AppLayout.
 */
export function useOnboardingState(): OnboardingState {
  const ctx = useContext(OnboardingContext);
  if (!ctx) {
    throw new Error('useOnboardingState must be used within an OnboardingProvider');
  }
  return ctx;
}

/**
 * Internal implementation — owns the actual state. Mounted exactly once
 * by OnboardingProvider so all useOnboardingState() callers share it.
 */
function useOnboardingStateInternal(): OnboardingState {
  const { user } = useAuth();
  const hasChecked = useRef(false);
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [onboardingType, setOnboardingType] = useState<OnboardingFlowType>('none');
  const [isLoading, setIsLoading] = useState(true);
  const [libraryCount, setLibraryCount] = useState(0);
  const [projectCount, setProjectCount] = useState(0);

  useEffect(() => {
    if (!user) {
      setIsLoading(false);
      return;
    }

    // Only check once per user session to avoid race conditions
    if (!hasChecked.current) {
      hasChecked.current = true;
      checkOnboardingStatus();
    }
  }, [user?.id]);

  const checkOnboardingStatus = async () => {
    if (!user) return;

    try {
      // Check preferences
      const { data: profile } = await supabase
        .from('profiles')
        .select('preferences')
        .eq('id', user.id)
        .single();

      const preferences = profile?.preferences as Record<string, unknown> | null;
      const onboardingCompleted = preferences?.onboarding_completed === true;
      const forceShowTutorial = preferences?.force_show_tutorial === true;

      // If force_show_tutorial is true, show onboarding immediately (full flow for replay)
      if (forceShowTutorial) {
        setShowOnboarding(true);
        setOnboardingType('full');
        setIsLoading(false);
        return;
      }

      if (onboardingCompleted) {
        setShowOnboarding(false);
        setOnboardingType('none');
        setIsLoading(false);
        return;
      }

      // Get user's organization membership to determine if they're the creator or invited
      const { data: membership } = await supabase
        .from('organization_members')
        .select('organization_id, role, joined_at')
        .eq('user_id', user.id)
        .not('joined_at', 'is', null)
        .order('joined_at', { ascending: true })
        .limit(1)
        .single();

      if (!membership) {
        // No org membership yet - show full onboarding
        setShowOnboarding(true);
        setOnboardingType('full');
        setIsLoading(false);
        return;
      }

      // Get organization creation time to compare
      const { data: org } = await supabase
        .from('organizations')
        .select('id, created_at')
        .eq('id', membership.organization_id)
        .single();

      // Check org-level content (not user-level)
      const [qaRes, caseRes, sectionRes, pricingRes, projectRes] = await Promise.all([
        supabase.from('content_library').select('*', { count: 'exact', head: true }).eq('organization_id', membership.organization_id),
        supabase.from('case_studies').select('*', { count: 'exact', head: true }).eq('organization_id', membership.organization_id),
        supabase.from('company_sections').select('*', { count: 'exact', head: true }).eq('organization_id', membership.organization_id),
        supabase.from('pricing_templates').select('*', { count: 'exact', head: true }).eq('organization_id', membership.organization_id),
        supabase.from('projects').select('*', { count: 'exact', head: true }).eq('organization_id', membership.organization_id),
      ]);

      const totalLibrary = (qaRes.count || 0) + (caseRes.count || 0) + (sectionRes.count || 0) + (pricingRes.count || 0);
      const totalProjects = projectRes.count || 0;

      setLibraryCount(totalLibrary);
      setProjectCount(totalProjects);

      // Determine if user is the org creator
      // Owner role + joined within 1 minute of org creation = likely creator
      const isOwner = membership.role === 'owner';
      const joinedAt = new Date(membership.joined_at!).getTime();
      const orgCreatedAt = org ? new Date(org.created_at).getTime() : 0;
      const joinedWithinMinuteOfCreation = Math.abs(joinedAt - orgCreatedAt) < 60000;
      
      const isOrgCreator = isOwner && joinedWithinMinuteOfCreation;
      const orgHasContent = totalProjects > 0 || totalLibrary > 0;

      if (isOrgCreator) {
        // Org creator - show full onboarding if no content yet
        if (orgHasContent) {
          setShowOnboarding(false);
          setOnboardingType('none');
        } else {
          setShowOnboarding(true);
          setOnboardingType('full');
        }
      } else {
        // Invited member - show abbreviated onboarding
        setShowOnboarding(true);
        setOnboardingType('invited');
      }
    } catch (error) {
      console.error('Error checking onboarding status:', error);
      setShowOnboarding(false);
      setOnboardingType('none');
    } finally {
      setIsLoading(false);
    }
  };

  const completeOnboarding = useCallback(async () => {
    if (!user) return;

    try {
      // Get existing preferences
      const { data: profile } = await supabase
        .from('profiles')
        .select('preferences')
        .eq('id', user.id)
        .single();

      const existingPreferences = (profile?.preferences as Record<string, unknown>) || {};

      // Update preferences with onboarding completion and clear force_show_tutorial
      await supabase
        .from('profiles')
        .update({
          preferences: {
            ...existingPreferences,
            onboarding_completed: true,
            onboarding_completed_at: new Date().toISOString(),
            force_show_tutorial: false,
          },
        })
        .eq('id', user.id);

      setShowOnboarding(false);
    } catch (error) {
      console.error('Error completing onboarding:', error);
    }
  }, [user]);

  // For regular users - replay tutorial without deleting data
  const replayTutorial = useCallback(async () => {
    if (!user) return;
    setOnboardingType('full');
    setShowOnboarding(true);
    hasChecked.current = false;
  }, [user]);

  // For developers - wipe all data and reset onboarding
  const developerReset = useCallback(async () => {
    if (!user) return;

    try {
      // Delete all user data in the correct order (respecting foreign keys)
      // 1. Delete analytics and attribution first
      await supabase.from('quick_fix_analytics').delete().neq('id', '00000000-0000-0000-0000-000000000000');
      await supabase.from('library_attribution').delete().eq('user_id', user.id);
      
      // 2. Delete simulation results
      await supabase.from('simulation_results').delete().eq('user_id', user.id);
      
      // 3. Delete proposal sections (depends on projects)
      const { data: userProjects } = await supabase
        .from('projects')
        .select('id')
        .eq('user_id', user.id);
      
      if (userProjects && userProjects.length > 0) {
        const projectIds = userProjects.map(p => p.id);
        
        // Delete proposal section versions first (depends on proposal_sections)
        const { data: sections } = await supabase
          .from('proposal_sections')
          .select('id')
          .in('project_id', projectIds);
        
        if (sections && sections.length > 0) {
          const sectionIds = sections.map(s => s.id);
          await supabase.from('proposal_section_versions').delete().in('proposal_section_id', sectionIds);
        }
        
        await supabase.from('proposal_sections').delete().in('project_id', projectIds);
        
        // Delete project clarifications
        await supabase.from('project_clarifications').delete().in('project_id', projectIds);
        
        // Delete project contacts & links
        await supabase.from('project_contacts').delete().in('project_id', projectIds);
        await supabase.from('project_links').delete().in('project_id', projectIds);
        
        // Delete project outline data
        await supabase.from('project_outline_subsections').delete().in('project_id', projectIds);
        await supabase.from('project_outline_config').delete().in('project_id', projectIds);
        await supabase.from('project_outline').delete().in('project_id', projectIds);
        
        // Delete RFP extracted data
        await supabase.from('rfp_extracted_data').delete().in('project_id', projectIds);
        
        // Delete comments (depends on questions and projects)
        await supabase.from('comments').delete().in('project_id', projectIds);
        
        // Get questions and delete answers/versions
        const { data: questions } = await supabase
          .from('questions')
          .select('id')
          .in('project_id', projectIds);
        
        if (questions && questions.length > 0) {
          const questionIds = questions.map(q => q.id);
          
          // Delete answer versions first
          const { data: answers } = await supabase
            .from('answers')
            .select('id')
            .in('question_id', questionIds);
          
          if (answers && answers.length > 0) {
            const answerIds = answers.map(a => a.id);
            await supabase.from('answer_versions').delete().in('answer_id', answerIds);
          }
          
          await supabase.from('answers').delete().in('question_id', questionIds);
          await supabase.from('comments').delete().in('question_id', questionIds);
        }
        
        // Delete questions
        await supabase.from('questions').delete().in('project_id', projectIds);
        
        // Delete project watchers
        await supabase.from('project_watchers').delete().in('project_id', projectIds);
      }
      
      // 8. Delete notifications
      await supabase.from('notifications').delete().eq('user_id', user.id);
      
      // 9. Delete projects
      await supabase.from('projects').delete().eq('user_id', user.id);
      
      // 10. Delete library content and related data
      // Get org ID for org-scoped tables
      const { data: membership } = await supabase
        .from('organization_members')
        .select('organization_id')
        .eq('user_id', user.id)
        .limit(1)
        .single();
      
      const orgId = membership?.organization_id;
      
      // Library item comments & activity (org-scoped via item lookup, delete by user)
      await supabase.from('library_item_comments').delete().eq('user_id', user.id);
      await supabase.from('library_item_activity').delete().eq('user_id', user.id);
      
      // Library attachments (org-scoped)
      if (orgId) {
        await supabase.from('library_attachments').delete().eq('organization_id', orgId);
        await supabase.from('library_documents').delete().eq('organization_id', orgId);
      }
      
      await supabase.from('content_library').delete().eq('user_id', user.id);
      await supabase.from('case_studies').delete().eq('user_id', user.id);
      await supabase.from('company_sections').delete().eq('user_id', user.id);
      await supabase.from('pricing_templates').delete().eq('user_id', user.id);
      await supabase.from('team_members').delete().eq('user_id', user.id);
      
      // 11. Delete iterate sessions
      await supabase.from('iterate_sessions').delete().eq('user_id', user.id);
      
      // 12. Delete org notes
      if (orgId) {
        await supabase.from('org_notes').delete().eq('organization_id', orgId);
      }
      
      // 13. Delete report schedules
      await supabase.from('report_schedules').delete().eq('user_id', user.id);

      // 12. Reset onboarding preferences
      const { data: profile } = await supabase
        .from('profiles')
        .select('preferences')
        .eq('id', user.id)
        .single();

      const existingPreferences = (profile?.preferences as Record<string, Json>) || {};
      delete existingPreferences.onboarding_completed;
      delete existingPreferences.onboarding_completed_at;
      delete existingPreferences.force_show_tutorial;

      await supabase
        .from('profiles')
        .update({ preferences: existingPreferences as Json })
        .eq('id', user.id);

      setLibraryCount(0);
      setProjectCount(0);
      setShowOnboarding(true);
    } catch (error) {
      console.error('Error in developer reset:', error);
      throw error;
    }
  }, [user]);

  return {
    showOnboarding,
    onboardingType,
    isLoading,
    libraryCount,
    projectCount,
    completeOnboarding,
    replayTutorial,
    developerReset,
  };
}

/**
 * Provider — mount ONCE near the top of the tree (inside Router/Auth).
 * Owns the single onboarding state instance shared by every consumer.
 */
export function OnboardingProvider({ children }: { children: ReactNode }) {
  const value = useOnboardingStateInternal();
  return createElement(OnboardingContext.Provider, { value }, children);
}
